import React, { useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Receipt, CalendarDays, Plus, LayoutDashboard, MoreHorizontal,
  Scan, ImagePlus, Plane, Building2, Settings as SettingsIcon, LogOut,
} from 'lucide-react';
import BottomSheet from './BottomSheet';
import { useAuth } from '../../context/AuthContext';
import { useCapture } from '../../context/CaptureContext';

function Tab({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium ${
          isActive ? 'text-blue-600' : 'text-gray-500'
        }`
      }
    >
      <Icon className="w-6 h-6" />
      {label}
    </NavLink>
  );
}

function SheetAction({ icon: Icon, title, subtitle, onClick, tone = 'blue' }) {
  const tones = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    gray: 'bg-gray-100 text-gray-600',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-4 p-3 -mx-3 rounded-xl text-left active:bg-gray-100"
    >
      <span className={`w-12 h-12 rounded-xl flex items-center justify-center ${tones[tone]}`}>
        <Icon className="w-6 h-6" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-medium text-gray-900">{title}</span>
        {subtitle && <span className="block text-sm text-gray-500">{subtitle}</span>}
      </span>
    </button>
  );
}

/**
 * Fixed bottom tab bar for the mobile shell, with the centre ＋ action sheet
 * (scan / upload / new trip) and the "More" sheet (clients, settings, logout).
 */
function BottomNav() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { openScanner, startFromFile } = useCapture();
  const [captureOpen, setCaptureOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const fileInputRef = useRef(null);

  const go = (path) => {
    setMoreOpen(false);
    setCaptureOpen(false);
    navigate(path);
  };

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 pb-[env(safe-area-inset-bottom)]"
        aria-label="Main navigation"
      >
        <div className="flex items-stretch h-16">
          <Tab to="/expenses" icon={Receipt} label="Expenses" />
          <Tab to="/trips" icon={CalendarDays} label="Trips" />
          <div className="flex-1 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setCaptureOpen(true)}
              className="-mt-6 w-14 h-14 rounded-full bg-blue-600 text-white shadow-lg ring-4 ring-white flex items-center justify-center active:scale-95 transition-transform"
              aria-label="Add receipt or trip"
            >
              <Plus className="w-7 h-7" />
            </button>
          </div>
          <Tab to="/dashboard" icon={LayoutDashboard} label="Dashboard" />
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium text-gray-500"
          >
            <MoreHorizontal className="w-6 h-6" />
            More
          </button>
        </div>
      </nav>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) {
            setCaptureOpen(false);
            startFromFile(file);
          }
        }}
      />

      <BottomSheet open={captureOpen} onClose={() => setCaptureOpen(false)} title="Add">
        <div className="space-y-1">
          <SheetAction
            icon={Scan}
            title="Scan receipt"
            subtitle="Use the camera"
            onClick={() => { setCaptureOpen(false); openScanner(); }}
          />
          <SheetAction
            icon={ImagePlus}
            tone="green"
            title="Upload from photos or files"
            subtitle="Image or PDF"
            onClick={() => fileInputRef.current?.click()}
          />
          <SheetAction
            icon={Plane}
            tone="purple"
            title="New trip"
            subtitle="Dates, destinations, overnight allowance"
            onClick={() => go('/trips/new')}
          />
        </div>
      </BottomSheet>

      <BottomSheet open={moreOpen} onClose={() => setMoreOpen(false)} title={user?.username || 'More'}>
        <div className="space-y-1">
          <SheetAction icon={Building2} tone="gray" title="Clients & facilities" onClick={() => go('/clients')} />
          <SheetAction icon={SettingsIcon} tone="gray" title="Settings" onClick={() => go('/settings')} />
          <SheetAction
            icon={LogOut}
            tone="gray"
            title="Log out"
            onClick={() => { setMoreOpen(false); logout(); }}
          />
        </div>
      </BottomSheet>
    </>
  );
}

export default BottomNav;
